import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { availableGames } from '../data/games';
import { platformsData } from '../data/platforms';
import { ossGames } from '../data/appsData';
import { Badge } from '../components/ui';
import { PlatformIcon, PlayIcon, ArrowRight } from '../components/common/Icons';

const ARCADE_GAMES = [
  {
    slug: 'maasai-jump',
    title: 'Maasai Jump',
    desc: 'Endless runner — dodge rocks, spears & birds with double-jump!',
    emoji: '🏃',
    genre: 'Endless Runner',
    color: 'from-orange-600 to-red-700',
    accent: '#ff6b35',
  },
  {
    slug: 'savanna-snake',
    title: 'Savanna Snake',
    desc: 'Classic snake with African wildlife as collectible food!',
    emoji: '🐍',
    genre: 'Snake',
    color: 'from-green-700 to-emerald-900',
    accent: '#00d4aa',
  },
  {
    slug: 'spirit-memory',
    title: 'Spirit Memory',
    desc: 'Match African spirit symbols in this elegant memory game!',
    emoji: '✨',
    genre: 'Memory',
    color: 'from-purple-800 to-indigo-900',
    accent: '#9B59B6',
  },
  {
    slug: 'bongo-rhythm',
    title: 'Bongo Rhythm',
    desc: 'Watch the drums flash and repeat the pattern — Simon Says!',
    emoji: '🥁',
    genre: 'Rhythm',
    color: 'from-yellow-700 to-orange-800',
    accent: '#ffd700',
  },
  {
    slug: 'nyama-choma-catch',
    title: 'Nyama Choma Catch',
    desc: 'Catch falling food on your grill and avoid hazards!',
    emoji: '🍖',
    genre: 'Catch Game',
    color: 'from-red-800 to-rose-900',
    accent: '#E74C3C',
  },
  {
    slug: 'safari-shooter',
    title: 'Safari Shooter',
    desc: 'Space Invaders meets the Savanna — shoot to protect!',
    emoji: '🦁',
    genre: 'Space Invaders',
    color: 'from-teal-700 to-cyan-900',
    accent: '#00BCD4',
  },
  {
    slug: 'kilimanjaro-climber',
    title: 'Kilimanjaro Climber',
    desc: 'Bounce on platforms and climb to the peak! Doodle Jump style.',
    emoji: '⛰️',
    genre: 'Platformer',
    color: 'from-blue-700 to-indigo-800',
    accent: '#3498DB',
  },
  {
    slug: 'tinga-tinga-puzzle',
    title: 'Tinga Tinga Puzzle',
    desc: 'Slide colorful African tiles into order — classic 15-puzzle!',
    emoji: '🦋',
    genre: 'Puzzle',
    color: 'from-lime-700 to-green-900',
    accent: '#27AE60',
  },
  {
    slug: 'baobab-blocks',
    title: 'Baobab Blocks',
    desc: 'African-themed Tetris with vibrant Tinga Tinga colors!',
    emoji: '🌳',
    genre: 'Tetris',
    color: 'from-amber-700 to-orange-900',
    accent: '#F39C12',
  },
  {
    slug: 'dhow-dodge',
    title: 'Dhow Dodge',
    desc: 'Steer your dhow through ocean dangers and collect treasure!',
    emoji: '⛵',
    genre: 'Dodge',
    color: 'from-sky-700 to-blue-900',
    accent: '#2980B9',
  },
];

const GameModal = ({ game, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{game.emoji}</span>
          <div>
            <h2 className="font-gaming font-bold text-white text-base leading-none">{game.title}</h2>
            <p className="text-gray-500 text-xs mt-0.5">{game.genre} · Riven Byte Studios</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`/games/${game.slug}/index.html`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-400 hover:text-white border border-white/20 hover:border-white/50 px-3 py-1.5 rounded-lg transition-colors"
          >
            Open Full Screen ↗
          </a>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white text-lg transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
      {/* Game iframe */}
      <div className="flex-1 flex items-center justify-center bg-black p-4">
        <iframe
          src={`/games/${game.slug}/index.html`}
          title={game.title}
          className="w-full h-full max-w-5xl border-0 rounded-lg"
          style={{ maxHeight: 'calc(100vh - 70px)' }}
          allow="autoplay"
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    </div>
  );
};

const ArcadeCard = ({ game, onPlay }) => (
  <div
    className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-white/30 transition-all duration-300 cursor-pointer bg-black"
    onClick={() => onPlay(game)}
  >
    {/* Background gradient */}
    <div className={`absolute inset-0 bg-gradient-to-br ${game.color} opacity-40 group-hover:opacity-60 transition-opacity`} />
    {/* Pattern overlay */}
    <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)'
    }} />
    <div className="relative p-5 flex flex-col h-full min-h-[172px]">
      {/* Top row */}
      <div className="flex items-start justify-between mb-3">
        <span className="text-4xl drop-shadow-lg">{game.emoji}</span>
        <span
          className="text-xs px-2.5 py-1 rounded-full font-medium border"
          style={{ color: game.accent, borderColor: game.accent + '40', background: game.accent + '15' }}
        >
          {game.genre}
        </span>
      </div>
      {/* Title */}
      <h3 className="font-gaming font-bold text-white text-base leading-tight mb-1.5 group-hover:text-white transition-colors">
        {game.title}
      </h3>
      <p className="text-gray-400 text-xs leading-relaxed flex-1">{game.desc}</p>
      {/* CTA */}
      <div className="mt-4 flex items-center gap-2">
        <div
          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-black text-xs font-bold group-hover:scale-105 transition-transform"
          style={{ background: `linear-gradient(135deg, ${game.accent}, ${game.accent}cc)` }}
        >
          <PlayIcon className="w-3.5 h-3.5" />
          PLAY NOW
        </div>
      </div>
    </div>
  </div>
);

const PlayNowPage = () => {
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [activeGame, setActiveGame] = useState(null);

  const filteredGames =
    selectedPlatform === 'all'
      ? availableGames
      : availableGames.filter((game) => game.platforms.includes(selectedPlatform));

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Game Modal */}
      {activeGame && <GameModal game={activeGame} onClose={() => setActiveGame(null)} />}

      {/* Hero */}
      <section className="py-14 md:py-20 relative african-pattern">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="section-subtitle text-primary-500">INSTANT PLAY</p>
          <h1 className="section-title mb-4">
            <span className="text-gradient">PLAY NOW</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            10 free HTML5 games — playable directly in your browser. No download needed!
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* ═══════════════════════════════════════════════════════════
            HTML5 ARCADE SECTION
        ═══════════════════════════════════════════════════════════ */}
        <section className="mb-24">
          {/* Section header */}
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
            <div>
              <p className="text-primary-500 text-xs font-bold tracking-widest uppercase mb-1">Free to Play · No Download</p>
              <h2 className="text-3xl md:text-4xl font-bold">
                🎮 HTML5 <span className="text-gradient">ARCADE</span>
              </h2>
              <p className="text-gray-400 mt-2 text-sm">10 games made by Riven Byte Studios — play instantly in your browser</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 border border-white/10 rounded-xl px-4 py-2 bg-white/3">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse inline-block" />
              All games available now
            </div>
          </div>

          {/* Arcade grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {ARCADE_GAMES.map((game) => (
              <ArcadeCard key={game.slug} game={game} onPlay={setActiveGame} />
            ))}
          </div>

          {/* Credits bar */}
          <div className="mt-8 p-4 rounded-2xl bg-white/3 border border-white/8 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <p className="text-gray-400 text-xs">
                <span className="text-white font-medium">Developer:</span> Paul Nkingwa Thomas &nbsp;·&nbsp;
                <span className="text-white font-medium">Art Director:</span> Andrew Katabalo
              </p>
              <p className="text-gray-600 text-xs mt-0.5">© 2025 Riven Byte Studios · Made in Tanzania 🇹🇿</p>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">HTML5 Canvas</span>
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">Mobile Ready</span>
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">High Scores</span>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            MOBILE / STORE GAMES
        ═══════════════════════════════════════════════════════════ */}
        <section>
          <div className="mb-10">
            <p className="text-secondary-400 text-xs font-bold tracking-widest uppercase mb-1">Download & Play</p>
            <h2 className="text-3xl font-bold">
              STORE <span className="text-gradient">GAMES</span>
            </h2>
          </div>

          {/* Platform Filter */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedPlatform('all')}
                className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                  selectedPlatform === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                All Platforms
              </button>
              {platformsData.map((platform) => (
                <button
                  key={platform.id}
                  onClick={() => setSelectedPlatform(platform.id)}
                  className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center gap-2 ${
                    selectedPlatform === platform.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <PlatformIcon platform={platform.id} className="w-4 h-4" />
                  {platform.shortName}
                </button>
              ))}
            </div>
          </div>

          {/* Games Grid */}
          {filteredGames.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8">
              {filteredGames.map((game) => (
                <div
                  key={game.id}
                  className="group rounded-3xl overflow-hidden bg-white/5 border border-white/10 hover:border-primary-500/50 transition-all"
                >
                  <div className="flex flex-col md:flex-row">
                    <div
                      className="w-full md:w-48 h-48 md:h-auto relative flex-shrink-0"
                      style={{ background: game.image }}
                    >
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center group-hover:scale-110 transition-transform">
                          <PlayIcon className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 p-6">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <h3 className="font-gaming text-xl font-bold group-hover:text-primary-500 transition-colors">
                            {game.title}
                          </h3>
                          <p className="text-gray-400 text-sm mt-1">{game.tagline}</p>
                        </div>
                        {game.rating && (
                          <div className="flex items-center gap-1 text-yellow-400">
                            <span>★</span>
                            <span className="text-sm font-bold">{game.rating}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {game.genres.slice(0, 3).map((genre) => (
                          <Badge key={genre} variant="default" size="sm">{genre}</Badge>
                        ))}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          {game.platforms.map((platform) => (
                            <div
                              key={platform}
                              className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-gray-400"
                            >
                              <PlatformIcon platform={platform} className="w-4 h-4" />
                            </div>
                          ))}
                        </div>
                        <Link
                          to={`/games/${game.slug}`}
                          className="flex items-center gap-2 text-primary-500 font-medium hover:gap-3 transition-all"
                        >
                          View Game
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                      {game.downloads && (
                        <p className="text-gray-500 text-xs mt-3">{game.downloads} downloads</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🎮</div>
              <h3 className="text-2xl font-bold mb-2">No games available</h3>
              <p className="text-gray-400 mb-6">No games available for this platform yet. Check back soon!</p>
              <button onClick={() => setSelectedPlatform('all')} className="btn-primary">
                View All Platforms
              </button>
            </div>
          )}
        </section>

        {/* ═══════════════════════════════════════════════════════════
            OPEN SOURCE ARCADE SECTION
        ═══════════════════════════════════════════════════════════ */}
        <section className="mt-24">
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
            <div>
              <p className="text-yellow-400 text-xs font-bold tracking-widest uppercase mb-1">Community · Open Source</p>
              <h2 className="text-3xl md:text-4xl font-bold">
                🌍 Classic <span className="text-gradient">Arcade</span>
              </h2>
              <p className="text-gray-400 mt-2 text-sm">
                10 beloved open-source games, cloned & curated — click to play in a new tab
              </p>
            </div>
            <a
              href="https://github.com/michelpereira/awesome-open-source-games"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 border border-white/10 hover:border-white/25 rounded-xl px-4 py-2 transition-colors hover:text-white"
            >
              More open-source games ↗
            </a>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {ossGames.map((game) => (
              <a
                key={game.id}
                href={game.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative rounded-2xl overflow-hidden border border-white/8 hover:border-white/25 transition-all duration-300 bg-black"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${game.color} opacity-30 group-hover:opacity-50 transition-opacity`} />
                <div className="relative p-5 flex flex-col min-h-[160px]">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-3xl">{game.emoji}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-black/40 border border-white/10 text-gray-400">
                      {game.license}
                    </span>
                  </div>
                  <h3 className="font-gaming font-bold text-white text-sm leading-tight mb-1">{game.title}</h3>
                  <p className="text-gray-500 text-xs">{game.genre}</p>
                  <p className="text-gray-600 text-xs mt-1">by {game.author}</p>
                  <div className="mt-auto pt-3 flex items-center justify-between">
                    <span
                      className="text-xs font-bold group-hover:gap-1.5 flex items-center gap-1 transition-all"
                      style={{ color: game.accent }}
                    >
                      PLAY ↗
                    </span>
                    <a
                      href={game.repoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-600 hover:text-white text-xs transition-colors"
                    >
                      GitHub
                    </a>
                  </div>
                </div>
              </a>
            ))}
          </div>
          <p className="text-center text-gray-600 text-xs mt-5">
            All open-source games are the property of their respective authors. Linked under their original open-source licenses.
          </p>
        </section>

        {/* Coming Soon */}
        <section className="mt-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Coming Soon</h2>
            <p className="text-gray-400">More African adventures on the way. Wishlist these upcoming titles!</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Savanna Warriors', date: 'Q4 2025', image: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)' },
              { title: 'Kilimanjaro Quest', date: 'Q1 2026', image: 'linear-gradient(135deg, #00d4aa 0%, #00a86b 100%)' },
              { title: 'Drums of War', date: 'Q2 2026', image: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' },
            ].map((game) => (
              <div key={game.title} className="rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                <div className="h-32 relative" style={{ background: game.image }}>
                  <div className="absolute inset-0 bg-black/40" />
                  <Badge variant="coming-soon" className="absolute top-4 right-4">{game.date}</Badge>
                </div>
                <div className="p-4">
                  <h3 className="font-bold">{game.title}</h3>
                  <button className="mt-3 text-sm text-primary-500 hover:underline">+ Add to Wishlist</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PlayNowPage;
